import { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, ShieldOff, Monitor, Smartphone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { inputCls, btn, btnLight, Card, H, errMsg } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ah = () => ({ withCredentials: true });
const fmt = (iso) => new Date(iso).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });

const PasswordCard = ({ l }) => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.put(`${API}/account/password`, f, ah()); toast.success(l.changed); setF({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="account-password">
      <H>{l.pwTitle}</H>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="pw-current" required type="password" className={inputCls} placeholder={l.current} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
        <input data-testid="pw-new" required minLength={8} type="password" className={inputCls} placeholder={l.next} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
        <button data-testid="pw-submit" disabled={busy} className={btn}>{l.change}</button>
      </form>
    </Card>
  );
};

const TwoFACard = ({ user, refreshUser, l }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = !!user?.twofa_enabled;
  const start = async () => { try { setSetup((await axios.post(`${API}/auth/2fa/setup`, {}, ah())).data); } catch (e) { toast.error(errMsg(e)); } };
  const act = async (path, msg) => {
    setBusy(true);
    try { await axios.post(`${API}/auth/2fa/${path}`, { code }, ah()); toast.success(msg); setSetup(null); setCode(""); refreshUser(); }
    catch (e) { toast.error(errMsg(e, "Code invalide")); } finally { setBusy(false); }
  };
  return (
    <Card data-testid="account-2fa">
      <H>{l.twofa}</H>
      <p className="flex items-start gap-2 text-sm text-stone-700" data-testid="account-2fa-status">{enabled ? <ShieldCheck size={16} className="text-[#4A624E] shrink-0 mt-0.5" /> : <ShieldOff size={16} className="text-[#C85A32] shrink-0 mt-0.5" />}{enabled ? l.on : l.off}</p>
      {!enabled && !setup && <button data-testid="account-2fa-start" onClick={start} className={`${btn} mt-4`}>{l.enable}</button>}
      {!enabled && setup && (
        <div className="mt-4 grid sm:grid-cols-[160px_1fr] gap-5 items-start" data-testid="account-2fa-setup">
          <div className="bg-white p-2 rounded-xl border border-stone-200 inline-block"><QRCodeSVG value={setup.otpauth_uri} size={144} /></div>
          <div className="space-y-2 text-sm text-stone-600">
            <p>{l.step1}</p><p>{l.step2} <code className="font-mono text-xs bg-stone-100 px-2 py-1 rounded break-all" data-testid="account-2fa-secret">{setup.secret}</code></p><p>{l.step3}</p>
            <div className="flex flex-wrap gap-2"><input data-testid="account-2fa-code" className={`${inputCls} !w-36 font-mono tracking-widest`} maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} /><button data-testid="account-2fa-enable" disabled={busy || code.length !== 6} onClick={() => act("enable", l.enabled)} className={btn}>{l.confirm}</button><button onClick={() => setSetup(null)} className={btnLight}>{l.cancel}</button></div>
          </div>
        </div>
      )}
      {enabled && <div className="mt-4 flex flex-wrap items-center gap-2 text-sm"><span className="text-stone-600">{l.disableHint}</span><input data-testid="account-2fa-disable-code" className={`${inputCls} !w-32 font-mono`} maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} /><button data-testid="account-2fa-disable" disabled={busy || code.length !== 6} onClick={() => act("disable", l.disabled)} className={btnLight}>{l.disable}</button></div>}
    </Card>
  );
};

const SessionsCard = ({ l }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${API}/account/sessions`, ah()).then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => { await axios.delete(`${API}/account/sessions${id ? `/${id}` : ""}`, ah()); load(); };
  return (
    <Card data-testid="account-sessions">
      <H>{l.sessions}</H>
      <ul className="divide-y divide-stone-100 text-sm">
        {rows.map((s) => (
          <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`session-${s.id}`}>
            {/Mobi|Android|iPhone/i.test(s.user_agent) ? <Smartphone size={15} className="text-stone-400" /> : <Monitor size={15} className="text-stone-400" />}
            <div className="flex-1 min-w-0"><p className="truncate text-stone-800">{s.user_agent?.split(") ")[0]?.replace("Mozilla/5.0 (", "") || "—"} {s.current && <span className="ml-2 rounded-full bg-[#4A624E]/10 text-[#4A624E] px-2 py-0.5 text-[10px] font-mono">{l.thisDevice}</span>}</p><p className="text-[11px] text-stone-500 font-mono">{l.lastSeen} {fmt(s.last_seen)} · IP {s.ip}</p></div>
            {!s.current && <button data-testid={`session-revoke-${s.id}`} onClick={() => revoke(s.id)} className={btnLight}>{l.revoke}</button>}
          </li>
        ))}
      </ul>
      {rows.length > 1 && <button data-testid="sessions-revoke-all" onClick={() => revoke()} className={`${btnLight} mt-4`}>{l.revokeAll}</button>}
    </Card>
  );
};

export const SecurityPanel = ({ user, refreshUser, l }) => (
  <div className="grid lg:grid-cols-2 gap-6">
    <TwoFACard user={user} refreshUser={refreshUser} l={l} />
    <PasswordCard l={l} />
    <div className="lg:col-span-2"><SessionsCard l={l} /></div>
  </div>
);
